<template>
	<div v-if="details" class="qna-report right-memo-box">
		<PageHeader :title="title" />
		<ManagingSection :columnCount="2" header="1. 신고된 정보">
			<ManagingBox :title="`${typeConsts.label}번호`">
				<RouterLink
					:to="`${typeConsts.link}/${typeDetails.id}`"
					target="_blank"
					class="link"
					v-text="typeDetails.id"
				/>
			</ManagingBox>
			<ManagingBox
				v-if="hasParent"
				:title="`${parentTypeConsts.label}번호`"
			>
				<RouterLink
					:to="`${parentTypeConsts.link}/${typeDetails[`${typeConsts.parentRefType}_id`]}`"
					target="_blank"
					class="link"
					v-text="typeDetails[`${typeConsts.parentRefType}_id`]"
				/>
			</ManagingBox>
			<ManagingBox v-else title="작성자">
				<RouterLink
					:to="`/register/register-info/${typeDetails.register_id}`"
					target="_blank"
					class="link"
					v-text="typeDetails.register.nickName"
				/>
			</ManagingBox>
		</ManagingSection>
		<ManagingSection :splice="true">
			<ManagingBox v-if="hasParent" title="작성자">
				<RouterLink
					:to="`/register/register-info/${typeDetails.register_id}`"
					target="_blank"
					class="link"
					v-text="typeDetails.register.nickName"
				/>
			</ManagingBox>
			<ManagingBox v-else title="제목">
				{{ typeDetails.title }}
			</ManagingBox>
			<ManagingBox title="내용">
				<pre>{{ typeDetails.text }}</pre>
			</ManagingBox>
		</ManagingSection>
		<ManagingSection
			:columnCount="2"
			header="2. 신고한 내용"
			subHeader="※ [오신고] 처리시, 신고자의 오신고 횟수가 추가되며 회원상세에서 확인할 수 있습니다."
			:warningSubHeader="true"
		>
			<ManagingBox title="신고자" :horizontalMode="true">
				<RouterLink
					:to="`/register/register-info/${details.register.idRegister}`"
					target="_blank"
					class="link qna-report__invalid-report-link"
					v-text="details.register.nickName"
				/>
				(총 오신고 수: {{ details.invalidReportCount }}건)
			</ManagingBox>
			<ManagingBox title="신고번호">{{ details.id }}</ManagingBox>
			<ManagingBox title="신고일시">{{
				details.created_at | dateFormat
			}}</ManagingBox>
			<ManagingBox title="IP">{{ details.ip_address }}</ManagingBox>
			<ManagingBox title="신고사유">{{
				details.static_code.name
			}}</ManagingBox>
			<ManagingBox title="오신고">
				<template v-if="details.invalid_at">
					<p class="font-red">
						{{ details.invalid_at | dateFormat }}
					</p>
					<button
						class="btn orange"
						type="button"
						v-text="'오신고처리 해제'"
						@click="misreport()"
					/>
				</template>
				<button
					v-else
					:disabled="isBlockState"
					type="button"
					class="btn red"
					v-text="'오신고처리'"
					@click="misreport(true)"
				/>
			</ManagingBox>
		</ManagingSection>
		<ManagingSection :splice="true">
			<ManagingBox title="내용">
				<pre>{{ details.text }}</pre>
			</ManagingBox>
		</ManagingSection>
		<form @submit.prevent="putState">
			<ManagingSection :columnCount="2" header="3. 처리결과">
				<ManagingBox
					title="처리상태"
					:class="details.state | stateClass"
				>
					{{ details.state }}
				</ManagingBox>
				<ManagingBox title="신고횟수" :horizontalMode="true">
					{{ details.reportCount | number }}건
					<span class="color-green qna-report__report-count-desc"
						>※ 동일한 신고는 일괄처리 됩니다.</span
					>
				</ManagingBox>
			</ManagingSection>
			<ManagingSection :splice="true">
				<ManagingBox title="질문상태">
					<p
						v-if="!!typeDetails.deleted_at"
						class="font-red"
						v-text="`삭제된 ${typeConsts.label} 입니다.`"
					/>
					<RadioList
						v-else
						v-model="typeDetails.state"
						:radioList="stateList"
						:disabledIds="['검수']"
						:disabled="true"
					/>
				</ManagingBox>
				<ManagingBox
					v-if="isBlockState"
					title="차단사유"
					:horizontalMode="true"
					contsClass="qna-report__static-code"
				>
					<Selector
						v-model="staticCode1"
						:optionList="staticCodeList"
						valueKey="code"
					/>
					<Selector
						v-model="staticCode2"
						:optionList="staticCodeList"
						valueKey="code"
					/>
					<div
						v-if="!staticCode1"
						class="font-red qna-report__static-code-desc"
						v-text="'첫번째 사유는 필수로 선택해야 합니다!'"
					/>
				</ManagingBox>
			</ManagingSection>
			<div class="btn-wrap-main">
				<button
					type="button"
					class="btn-main"
					v-text="'취소'"
					@click="$router.go(-1)"
				/>
				<button
					:disabled="!canPutState"
					type="submit"
					class="btn-main blue"
					v-text="'상태변경'"
				/>
			</div>
		</form>
		<RightMemoBox
			refType="qna_report"
			:refId="parseInt($route.params._id)"
		/>
	</div>
</template>
<script>
	import compact from 'lodash/compact';
	import { mapState } from 'vuex';
	import { qnaReportType } from '@/constants.js';
	import PageHeader from '@/components/PageHeader.vue';
	import ManagingSection from '@/components/layout/ManagingSection.vue';
	import ManagingBox from '@/components/layout/ManagingBox.vue';
	import RadioList from '@/components/RadioList.vue';
	import Selector from '@/components/Selector.vue';
	import RightMemoBox from '@/components/RightMemoBox';

	export default {
		name: 'QnaReportDetail',
		components: {
			PageHeader,
			ManagingSection,
			ManagingBox,
			RadioList,
			Selector,
			RightMemoBox,
		},
		data() {
			return {
				title: '',
				details: null,
				typeDetails: null,
				originTypeDetailsState: '',
				staticCode1: null,
				staticCode2: null,
				stateList: [
					{ id: '정상', value: '정상' },
					{ id: '차단', value: '차단' },
					{ id: '검수', value: '검수' },
				],
			};
		},
		computed: {
			...mapState({
				staticCodeList: state =>
					state.filter.staticCodes.filter(
						item => item.parent_code === 'QNA_BLOCK'
					),
			}),
			typeConsts() {
				return qnaReportType[this.$route.params._type];
			},
			parentTypeConsts() {
				return qnaReportType[this.typeConsts.parentType];
			},
			hasParent() {
				return this.typeConsts.parentType;
			},
			isBlockState() {
				return this.typeDetails.state === '차단';
			},
			canPutState() {
				return this.isBlockState
					? this.staticCode1 &&
							this.typeDetails.static_code_list
								.map(({ code }) => code)
								.join(',') !==
								compact([
									this.staticCode1,
									this.staticCode2,
								]).join(',')
					: this.typeDetails.state !== this.originTypeDetailsState;
			},
		},
		created() {
			document.title =
				this.title = `${this.typeConsts.label} 신고 상세정보`;
			this.load();
		},
		methods: {
			async misreport(bool) {
				try {
					const invalid_at = bool ? new Date() : null;
					const message = bool
						? `신고번호: ${this.details.id}\n오신고 처리 하시겠습니까?\n\n오신고 처리시 해당 신고번호에 한하여 처리되며\n신고자에게 오신고 횟수가 추가 됩니다.`
						: `신고번호: ${this.details.id}\n오신고 처리를 히제 하시겠습니까?\n\n오신고 처리 해제시\n신고자의 오신고 횟수도 차감 됩니다.`;

					if (!confirm(message)) return;

					const params = { invalid_at };

					await this.$axiosAdmin2.put(
						`/api/qna/report/${this.details.id}`,
						params
					);

					this.$router.go(-1);
				} catch (e) {
					console.error(e);
					alert('처리하는데 실패했습니다.');
				}
			},
			async putState() {
				try {
					const params = {
						idList: [this.typeDetails.id],
						state: this.typeDetails.state,
					};

					if (this.isBlockState) {
						if (
							!confirm(
								`신고번호: ${this.details.id}\n질문상태: ${this.typeDetails.state}\n\n신고를 처리하시겠습니까?`
							)
						)
							return;
						params.staticCodeList = compact([
							this.staticCode1,
							this.staticCode2,
						]).map(code => ({ code }));
					}

					await this.$axiosAdmin2.put(
						`/api${this.typeConsts.api}/state`,
						params
					);

					this.$router.go(-1);
				} catch (e) {
					console.error(e);
					alert('상태변경하는데 실패했습니다.');
				}
			},
			async load() {
				try {
					const { data } = await this.$axiosAdmin2.get(
						`/api/qna/report/${this.$route.params._id}`
					);
					this.details = data.qna_report;
					this.typeDetails = this.details[this.typeConsts.refType];
					this.staticCode1 =
						this.typeDetails.static_code_list[0]?.code || null;
					this.staticCode2 =
						this.typeDetails.static_code_list[1]?.code || null;
					this.originTypeDetailsState = this.typeDetails.state;
				} catch (e) {
					console.error(e);
					alert('데이터를 불러오는데 실패했습니다.');
				}
			},
		},
	};
</script>
<style lang="scss" scoped>
	.qna-report {
		&__invalid-report-link {
			margin-right: 4px;
		}

		&__report-count-desc {
			margin-left: 4px;
		}

		&::v-deep {
			.qna-report__static-code {
				flex-wrap: wrap !important;
			}
		}

		&__static-code-desc {
			width: 100%;
			margin-top: 4px;
		}
	}
</style>
